import type { WithTechnique, CustomizationTechnique } from '@features/customization-technique/types';

/** No technique selected: everything is shown. */
export const ALL_TECHNIQUES = 'all';

export type TechniqueFilterValue = typeof ALL_TECHNIQUES | number;

/** A filter choice: a technique with the number of items (families, sessions…) that need it. */
export interface TechniqueOption extends CustomizationTechnique {
  count: number;
  express: boolean;
}

/** Techniques of the items, in priority order, with how many items each one has. */
export const getTechniqueOptions = (items: WithTechnique[]): TechniqueOption[] => {
  const options = new Map<number, TechniqueOption>();

  items.forEach(({ technique, express }) => {
    const option = options.get(technique.id) ?? { ...technique, count: 0, express: false };
    option.count += 1;
    option.express ||= express;
    options.set(technique.id, option);
  });

  return [...options.values()].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name));
};

/**
 * The filter actually applied: a remembered technique without any item right now falls back to all of them, so the
 * screen never shows a selection the operator cannot see.
 */
export const resolveTechniqueFilter = (
  filter: TechniqueFilterValue,
  options: TechniqueOption[]
): TechniqueFilterValue =>
  filter !== ALL_TECHNIQUES && options.some((option) => option.id === filter) ? filter : ALL_TECHNIQUES;

export const matchesTechnique = (item: Pick<WithTechnique, 'technique'>, filter: TechniqueFilterValue) =>
  filter === ALL_TECHNIQUES || item.technique.id === filter;

/** Static class names so Tailwind generates them; unknown colors fall back to a neutral dot. */
const DOT_CLASSES: Record<string, string> = {
  blue: 'bg-blue-500',
  azure: 'bg-sky-500',
  indigo: 'bg-indigo-500',
  purple: 'bg-purple-500',
  pink: 'bg-pink-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-400',
  lime: 'bg-lime-500',
  green: 'bg-green-500',
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  dark: 'bg-neutral-800',
};

export const getTechniqueDotClass = (color: string) => DOT_CLASSES[color] ?? 'bg-muted-foreground';
